// stages/FinalReportStage.js
import React from 'react';

const FinalReportStage = ({ analysisData }) => {
  const handleScheduleConsultation = () => {
    document.location='https://calendly.com/the-i3solutions-sales-team/spreadsheet-modernization-review';
  };

  const handleDownloadReport = () => {
    window.print();
  };

  // Function to get complexity level based on solution type
  const getComplexityLevel = (solutionType) => {
    switch (solutionType) {
      case 'SharePoint/Teams List':
        return 'Low';
      case 'Basic Web Form/App':
        return 'Medium';
      case 'Complex Web Application':
        return 'High';
      case 'Enterprise System':
        return 'Very High';
      default:
        return 'Medium';
    }
  };

  // Get the complexity label and CSS class
  const complexityLevel = getComplexityLevel(analysisData.recommendation?.solutionType);
  const complexityClass = complexityLevel.toLowerCase().replace(' ', '-');

  // Format timeline for display
  const formatTimeline = (timeline) => {
    if (!timeline) return 'To be determined';
    return timeline;
  };

  return (
    <div className="stage">
      <h2 className="stage-title">Your Analysis Report</h2>
      
      {/* Complete Analysis Results */}
      <div className="final-analysis">
        <div className="analysis-header">
          <h3>Analysis Summary for: {analysisData.fileName}</h3>
          <div className="complexity-badge">
            <span className="complexity-label">Complexity Level:</span>
            <span className={`complexity-score score-${complexityClass}`}>
              {complexityLevel}
            </span>
          </div>
        </div>
        
        <div className="analysis-grid">
          <div className="analysis-card">
            <h4>File Structure</h4>
            <div className="metric-list">
              <div className="metric">
                <span>Worksheets:</span>
                <span className="metric-value">{analysisData.worksheetCount}</span>
              </div>
              <div className="metric">
                <span>Total Rows:</span>
                <span className="metric-value">{analysisData.totalRows?.toLocaleString()}</span>
              </div>
              <div className="metric">
                <span>Total Columns:</span>
                <span className="metric-value">{analysisData.totalColumns?.toLocaleString()}</span>
              </div>
              <div className="metric">
                <span>Has Macros:</span>
                <span className="metric-value">{analysisData.hasMacros ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
          
          <div className="analysis-card">
            <h4>Complexity Analysis</h4>
            <div className="metric-list">
              <div className="metric">
                <span>Formula Complexity:</span>
                <span className="metric-value">{analysisData.formulaComplexity}</span>
              </div>
              <div className="metric">
                <span>Usage Pattern:</span>
                <span className="metric-value">{analysisData.usagePattern}</span>
              </div>
              <div className="metric">
                <span>Cross-sheet References:</span>
                <span className="metric-value">{analysisData.crossReferences}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Solution Recommendation */}
        {analysisData.recommendation && (
          <div className="recommendation-card">
            <h3>Recommended Approach</h3>
            <div className="recommendation-content">
              <div className="solution-type-header">
                <h4>{analysisData.recommendation.solutionType}</h4>
                <p className="solution-description">{analysisData.recommendation.description}</p>
              </div>
              
              <div className="recommendation-grid">
                <div className="rec-item">
                  <strong>Timeline:</strong>
                  <span>{formatTimeline(analysisData.recommendation.timeline)}</span>
                </div>
                <div className="rec-item">
                  <strong>Effort:</strong>
                  <span>{analysisData.recommendation.effort}</span>
                </div>
              </div>

              {/* Considerations */}
              {analysisData.recommendation.considerations && analysisData.recommendation.considerations.length > 0 && (
                <div className="considerations-section">
                  <h5>Key Considerations:</h5>
                  <ul className="considerations-list">
                    {analysisData.recommendation.considerations.map((consideration, index) => (
                      <li key={index}>{consideration}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Next Steps */}
              {analysisData.recommendation.nextSteps && analysisData.recommendation.nextSteps.length > 0 && (
                <div className="next-steps-section">
                  <h5>Next Steps:</h5>
                  <ol className="next-steps-list">
                    {analysisData.recommendation.nextSteps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Call to Action */}
        <div className="cta-section">
          <h4>Talk It Through With a Modernization Advisor</h4>
          <p>The analysis points to a {analysisData.recommendation?.solutionType || 'recommended solution'}. A short scoping call with a senior modernization advisor confirms the fit, the sequence, and what it realistically takes to get there.</p>
          <p className="cta-proof">Backed by 1,500+ Microsoft engagements.</p>
          <div className="cta-buttons">
            <button
              className="btn-primary"
              onClick={handleScheduleConsultation}
            >
              Schedule a Modernization Review
            </button>
            <button
              className="btn-secondary"
              onClick={handleDownloadReport}
            >
              Download Report
            </button>
          </div>
        </div>
      </div>

      {/* Additional CSS for new elements */}
      <style jsx>{`
        .solution-type-header {
          margin-bottom: 1.5rem;
        }
        
        .solution-type-header h4 {
          color: white;
          margin-bottom: 0.5rem;
          font-size: 1.25rem;
          font-weight: 700;
        }
        
        .solution-description {
          color: rgba(255, 255, 255, 0.9);
          font-style: italic;
          margin-bottom: 1rem;
          font-size: 1rem;
        }
        
        .considerations-section,
        .next-steps-section {
          margin-top: 1.5rem;
        }
        
        .considerations-section h5,
        .next-steps-section h5 {
          color: white;
          margin-bottom: 0.75rem;
          font-size: 1rem;
          font-weight: 600;
        }
        
        .considerations-list,
        .next-steps-list {
          margin: 0;
          padding-left: 1.5rem;
        }
        
        .considerations-list li,
        .next-steps-list li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.95);
          font-size: 0.95rem;
        }
        
        .recommendation-grid {
          margin-bottom: 1.5rem;
        }
        
        .rec-item {
          margin-bottom: 0.75rem;
        }
        
        .rec-item strong {
          color: white;
          font-weight: 600;
        }
        
        .rec-item span {
          color: rgba(255, 255, 255, 0.95);
          margin-left: 0.5rem;
        }
        
        .score-low {
          background-color: #10B981;
          color: white;
        }

        .score-medium {
          background-color: #F59E0B;
          color: white;
        }

        .score-high {
          background-color: #EF4444;
          color: white;
        }

        .score-very-high {
          background-color: #7C2D12;
          color: white;
        }

        .complexity-score {
          padding: 0.25rem 0.75rem;
          border-radius: 0.375rem;
          font-weight: 600;
          font-size: 0.875rem;
        }

        /* Improve overall recommendation card contrast */
        .recommendation-card {
          background: linear-gradient(135deg, #504DE2 0%, #0308A1 100%);
          border-radius: 12px;
          padding: 2rem;
          color: white;
          box-shadow: 0 10px 25px rgba(80, 77, 226, 0.25);
        }
        
        .recommendation-card h3 {
          color: white;
          margin-bottom: 1.5rem;
          font-size: 1.5rem;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default FinalReportStage;